import type { ColDef, IServerSideGetRowsRequest } from 'ag-grid-community'
import { toLower } from 'lodash-es'

import type {
  IAPICodeDTO,
  IApiSuccessWrapper,
  LineSegmentGenericData
} from '$/types'
import { apiSlice } from 'app/services/api'

/**
 * NOTE: There was once a dedicated AGGrid response microservice
 *       that is now closed off and proxied by the main backend
 *       API.
 *
 *       As the response interface is similar, we will continue
 *       to group the table API here unless interface deviates
 *       further.
 */

/**
 * Used to support cache busting / proper cache pinning
 * Does not get used by the API request, it is only here to
 * differentiate the method call signature which is cached
 */
interface ICacheKeys {
  cacheKeys: {
    systemScopeUpdatedAt: number
  }
}

export interface ILineSegments {
  ToMeasure: number
  FromMeasure: number
  ContinuousRouteId: number
  [key: string]: LineSegmentGenericData
}

/**
 * NOTE: This endpoint is also used for chart data..
 */
interface IDataManagementDefaultDataViewSSRMQuery extends ICacheKeys {
  params: {
    viewId: string
  }
  body: {
    // Would be better if it's part of query params..
    systemScopeId?: string
    sourceType: 'views' | 'charts'
  } & IServerSideGetRowsRequest
}

interface IDataManagementDefaultDataViewColDefsQuery extends ICacheKeys {
  params: {
    viewId: string
    systemScopeId?: string
  }
}

/**
 * ColDef from our server side table API with extra field information
 *
 * NOTE: all fields are optional to support interchangeability with ColDef
 */
export interface IExtendedColDef extends ColDef, Partial<IAPICodeDTO> {
  // #region Data type
  dataType?: 'bit' | 'int' | 'float' | 'date' | 'string'
  attributeEnums?: IAPICodeDTO[]
  minValue?: number
  maxValue?: number
  minDate?: string
  maxDate?: string
  // #endregion

  cbvAllowed?: boolean
  isDataQualityColumn?: boolean
  unitTypeName?: string
}

interface IColDefsAPISuccessResponse {
  tableName: string
  columnDefinitions: IExtendedColDef[]
}

interface IServerSideDatasourceSuccessParam {
  lastRow: number
  rows: ILineSegments[]
}

const ISOLATION_SECTION_RANGE_ROUNDED_FIELDS = new Set([
  'IsolationSectionLength',
  'IsolationSectionVolume'
])

const NON_EQUALITY_NUMBER_FILTER_OPTIONS = [
  'greaterThan',
  'lessThan',
  'inRange',
  'blank',
  'notBlank'
]

// group keys should be strings based on IServerSideGetRowsRequest.groupKeys
const nullifyGroupKeys = (key: string) =>
  key === '' || toLower(key) === 'null' ? null : key

/**
 * Place to keep all AG Grid related APIs
 * - Column Definitions
 * - Row Data
 */
export const agGridApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      'DataManagementDefaultDataViewColumnDefinition',
      'DataManagementDefaultDataViewRows',
      'SSFilterProfile'
    ]
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // #region Column Definitions
      // REIVEW: Look into possibility of adding addition param to
      //         toggle between default and custom view
      getDataManagementDefaultDataViewColumnDefinitions: build.query<
        IExtendedColDef[],
        IDataManagementDefaultDataViewColDefsQuery
      >({
        query: (query) => ({
          url: `/datamanagement/columndefinition/${query.params.viewId}`,
          params: {
            systemScopeId: query.params.systemScopeId
          }
        }),
        transformResponse: (
          res: IApiSuccessWrapper<IColDefsAPISuccessResponse>
        ) => {
          /**
           * NOTE: Returns most generic version of ColDef but this API's
           *       ColDef may contain additional property such as `unit`.
           */
          const colDefs = res.result.columnDefinitions.map((colDef) => {
            if (
              res.result.tableName !== 'IsolationSectionRange' ||
              !ISOLATION_SECTION_RANGE_ROUNDED_FIELDS.has(
                colDef.field || colDef.code || ''
              )
            ) {
              return colDef
            }

            return {
              ...colDef,
              filterParams: {
                ...colDef.filterParams,
                filterOptions: [...NON_EQUALITY_NUMBER_FILTER_OPTIONS]
              }
            }
          })

          return colDefs
        },
        providesTags: (_res, _err, query) => [
          {
            type: 'DataManagementDefaultDataViewColumnDefinition',
            id: query.params.viewId
          },
          {
            /**
             * NOTE: Reload when system scope is updated
             */
            type: 'SSFilterProfile',
            id: query.params.systemScopeId
          }
        ]
      }),
      // #endregion
      // #region Row Data
      getDataManagementDefaultDataViewTableRows: build.query<
        IServerSideDatasourceSuccessParam,
        IDataManagementDefaultDataViewSSRMQuery
      >({
        query: (ssrmQuery) => ({
          method: 'POST',
          url: `/datamanagement/dataview/${ssrmQuery.params.viewId}`,
          body: {
            ...ssrmQuery.body,
            groupKeys: ssrmQuery.body.groupKeys.map(nullifyGroupKeys)
          }
        }),
        transformResponse: (
          res: IApiSuccessWrapper<IServerSideDatasourceSuccessParam>
        ) => {
          return res.result
        },
        providesTags: (_res, _err, query) => [
          {
            type: 'DataManagementDefaultDataViewRows',
            id: query.params.viewId
          },
          {
            /**
             * Cache invalidation auto refetch seems to hit cache?
             * Workaround: pass additional param to avoid cache hit
             */
            type: 'SSFilterProfile',
            id: query.body.systemScopeId
          }
        ]
      })
      // #endregion
    })
  })

export const {
  useGetDataManagementDefaultDataViewColumnDefinitionsQuery,
  useLazyGetDataManagementDefaultDataViewTableRowsQuery
} = agGridApi
